// src/components/QuoteSummary.jsx
import React from 'react';

const dd = (a, b) => Math.max(1, Math.round((new Date(b) - new Date(a)) / 86400000));
const isTransportation = (it) => String(it.role || '').toLowerCase() === 'transportation';
const isTax = (it) => String(it.role || '').toLowerCase() === 'tax';

const isAccommodation = (it) => {
  const role = String(it.role || '').toLowerCase();
  const desc = String(it.description || '').toLowerCase();
  return (
    role === 'base' ||
    role === 'accommodation' ||
    role === 'stay' ||
    role === 'lodging' ||
    role === 'hotel' ||
    /chambre|room|héberg|logement/.test(desc)
  );
};

const safeNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

// ==== utilitaires (haut du fichier) ====
const parseMoneyMaybe = (v) => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const clean = v.replace(/\s|€|eur?/gi, '').replace(',', '.');
    const n = Number(clean);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

const pickArray = (maybe) => {
  if (Array.isArray(maybe)) return maybe;
  if (maybe && Array.isArray(maybe.items)) return maybe.items;
  if (maybe && Array.isArray(maybe.list)) return maybe.list;
  return [];
};

export function InlineLoader({ show, label = 'Recalcul du devis…' }) {
  if (!show) return null;

  return (
    <div
      style={{
        padding: '24px 0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        color: '#334155',
      }}
    >
      <div className="spinner-border spinner-border-sm mb-2" role="status" />
      <div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>
    </div>
  );
}

export default function QuoteSummary({
  product = {},
  search = {},
  price = {},
  extras = {},
  selectedRoom,
  selectedFlights,
  discount = 0,
  onContinue,
  quoteLoading = false,
}) {
  const title = product.hotelName || product.name || product.title || 'Séjour';
  const city = product.city || product.destination || '';
  const imgSrc = product?.imageUrl || product?.imgSrc || '';
  const currency = price?.currency || 'EUR';

  const normalizeMoney = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const roomDeltaFromState = normalizeMoney(extras?.roomDelta ?? extras?.room ?? 0);

  const fmtMoney = (v, c = 'EUR') =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: c }).format(Number(v || 0));

  /* 1) Base de travail: tableau normalisé (unit/total en €) */
  let itemsForDisplay = Array.isArray(price?.items)
    ? price.items.map((it) => ({
        ...it,
        unit: normalizeMoney(it?.unit),
        total: normalizeMoney(it?.total),
      }))
    : [];

  function formatDateLongFR(date) {
    if (!date) return '-';

    return new Intl.DateTimeFormat('fr-FR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(date);
  }

  const hasPGI =
    Array.isArray(price?.items) && price.items.length > 0 ? true : Number(price?.stay) > 0; // fallback si total direct

  /* 2) Mutations: Single × N (sans casser les prix) */
  // 🔽 juste au-dessus du "if (itemsForDisplay.length > 0)" :
  const normalizeRoomsSelection = (extras = {}) => {
    const cats = Array.isArray(extras.categories) ? extras.categories : [];
    const out = cats
      .map((c) => {
        const qty = 1;

        const unitRaw =
          (c.included && c.included.price != null ? c.included.price : null) ??
          (Array.isArray(c.options) && c.options[0]?.price != null
            ? c.options[0].price
            : null) ??
          0;

        // extras.categories.price semble déjà être en euros
        const unit = parseMoneyMaybe(unitRaw);

        const label =
          c.title ||
          c.included?.desc ||
          (Array.isArray(c.options) && c.options[0]?.desc) ||
          'Chambre';

        if (!unit || unit <= 0) return null;
        return { label, qty, unit };
      })
      .filter(Boolean);

    return out;
  };

  const hasPgiStayLines = itemsForDisplay.some((it) => {
    const role = String(it.role || '').toLowerCase();
    const ref = String(it.ref || '').toLowerCase();

    return role === 'base' && ref === 'stay';
  });

  // ✅ Important : si PGI a déjà donné les lignes STAY,
  // on ne remplace pas l’hébergement avec extras.categories
  if (itemsForDisplay.length > 0 && !hasPgiStayLines) {
    const chosenRooms = normalizeRoomsSelection(extras);

    if (chosenRooms.length > 0) {
      itemsForDisplay = itemsForDisplay.filter((it) => !isAccommodation(it));

      chosenRooms
        .slice()
        .reverse()
        .forEach((r, idx) => {
          const qty = Math.max(1, r.qty);
          const total = Number((r.unit * qty).toFixed(2));
          const label = qty > 1 ? `${qty} × ${r.label}` : r.label;

          itemsForDisplay.unshift({
            role: 'Base',
            code: `ROOM_${idx}`,
            ref: `ROOM_${idx}`,
            description: label,
            quantity: qty,
            unit: r.unit,
            total,
          });
        });
    }
  }

  // ✅ Important : si PGI a déjà donné les lignes STAY,
  // on ne remplace pas l’hébergement avec extras.categories
  if (itemsForDisplay.length > 0 && !hasPgiStayLines) {
    const chosenRooms = normalizeRoomsSelection(extras);

    if (chosenRooms.length > 0) {
      itemsForDisplay = itemsForDisplay.filter((it) => !isAccommodation(it));

      chosenRooms
        .slice()
        .reverse()
        .forEach((r, idx) => {
          const qty = Math.max(1, r.qty);
          const total = Number((r.unit * qty).toFixed(2));
          const label = qty > 1 ? `${qty} × ${r.label}` : r.label;

          itemsForDisplay.unshift({
            role: 'Base',
            code: `ROOM_${idx}`,
            ref: `ROOM_${idx}`,
            description: label,
            quantity: qty,
            unit: r.unit,
            total,
          });
        });
    }
  }
  const selectedPlanId =
    selectedFlights?.plan?.id ||
    selectedFlights?.plan?.code ||
    selectedFlights?.planId ||
    null;

  const pgiAlreadyHasSelectedFlight =
    !!selectedPlanId &&
    itemsForDisplay.some((it) => {
      const k = String(it.code || it.ref || '').trim();
      const s = String(selectedPlanId).trim();
      return isTransportation(it) && k === s;
    });

  const hasTransportLine = itemsForDisplay.some(isTransportation);

  if (
    hasPGI &&
    !hasTransportLine && // <-- important : on ne touche pas si PGI a déjà envoyé le transport
    (Number(selectedFlights?.transportDelta || 0) > 0 || selectedFlights?.plan) &&
    Array.isArray(itemsForDisplay) &&
    itemsForDisplay.length
  ) {
    // somme du transport de base (avant remplacement)
    const baseTransport = itemsForDisplay
      .filter(isTransportation)
      .reduce((s, it) => s + normalizeMoney(it.total), 0);

    const planPrice = selectedFlights?.plan?.price || null; // { total, unit?, qty?, description? }
    const supOnly = Number(selectedFlights?.transportTotal || 0);
    const chosenTransportTotal =
      planPrice?.total != null
        ? normalizeMoney(planPrice.total)
        : supOnly > 0
        ? supOnly
        : baseTransport;

    // on retire uniquement les Transportation, on garde les Tax
    itemsForDisplay = itemsForDisplay.filter((it) => !isTransportation(it));

    const descSrc =
      planPrice?.description || price.items.find(isTransportation)?.description || 'Vols';
    const unit = typeof planPrice?.unit === 'number' ? normalizeMoney(planPrice.unit) : null;
    const qty = typeof planPrice?.qty === 'number' ? Number(planPrice.qty) : null;
    let label = descSrc;
    if (unit != null && qty != null)
      label = `${descSrc} (${fmtMoney(unit, currency)} × ${qty})`;

    itemsForDisplay.push({
      role: 'Transportation',
      code: selectedFlights?.plan?.id || 'FLIGHTPLAN',
      ref: selectedFlights?.plan?.id || 'FLIGHTPLAN',
      description: label,
      quantity: null,
      unit: null,
      total: Number(chosenTransportTotal.toFixed(2)),
    });

    const lugTotal = Number(selectedFlights?.luggagesTotal || 0);
    if (lugTotal > 0) {
      itemsForDisplay.push({
        role: 'Supplement',
        code: 'LUGGAGE',
        ref: 'LUGGAGE',
        description: selectedFlights?.luggagesSelection?.label || 'Bagages',
        quantity: null,
        unit: null,
        total: Number(normalizeMoney(lugTotal).toFixed(2)),
      });
    }
  }

  const hasBoardLine = (items = []) =>
    items.some((it) => {
      const role = String(it.role || '').toLowerCase();
      const kind = String(it.kind || '').toLowerCase();
      const code = String(it.code || it.ref || '').toUpperCase();
      const desc = String(it.description || '').toLowerCase();

      return (
        kind === 'board' || // ton payload montre kind:"board"
        code === 'ADIFMPC' || // exemple (tu peux élargir)
        /pension|meal|board|demi|compl[eè]te/.test(desc) ||
        role === 'board'
      );
    });

  const meal = extras?.mealPlan;

  // on retire une ancienne ligne MEALPLAN si elle existe
  itemsForDisplay = itemsForDisplay.filter(
    (it) => String(it.code || it.ref || '').toUpperCase() !== 'MEALPLAN',
  );

  const pgiAlreadyHasMeal = hasPGI && hasBoardLine(itemsForDisplay);

  // ✅ on n'ajoute le supplément mealPlan que si PGI ne l'a pas déjà envoyé
  if (hasPGI && meal && !meal.included && !pgiAlreadyHasMeal) {
    const mealTotal = normalizeMoney(meal.total);

    if (mealTotal > 0) {
      itemsForDisplay.push({
        role: 'Supplement',
        code: 'MEALPLAN',
        ref: 'MEALPLAN',
        description: meal.desc || 'Supplément pension',
        quantity: null,
        unit: null,
        total: Number(mealTotal.toFixed(2)),
      });
    }
  }

  // ✅ Prestations optionnelles sélectionnées
  const selectedPrestationsList = pickArray(extras?.prestations || extras?.selectedPrestations)
    .map((p, idx) => {
      const qty = Math.max(1, Number(p.qty || p.quantity || 1));
      const unit = normalizeMoney(p.unitPrice ?? p.unit ?? p.price?.unit ?? 0);

      const total =
        p.total != null ? normalizeMoney(p.total) : Number((unit * qty).toFixed(2));

      return {
        code: p.code || p.optionId || `ADDON_${idx}`,
        label:
          p.groupLabel && p.label
            ? `${p.groupLabel} - ${p.label}`
            : p.label || p.description || 'Prestation',
        qty,
        unit,
        total,
        currency: p.currency || currency,
      };
    })
    .filter((p) => Number(p.total || 0) > 0);

  const prestationsFromState = Number(
    (extras?.prestationsTotal != null
      ? normalizeMoney(extras.prestationsTotal)
      : selectedPrestationsList.reduce((s, p) => s + Number(p.total || 0), 0)
    ).toFixed(2),
  );

  const alreadyHasPrestationsLine = itemsForDisplay.some((it) => {
    const kind = String(it.kind || '').toLowerCase();
    const code = String(it.code || it.ref || '').toLowerCase();

    return (
      kind === 'addon' ||
      code === 'addons' ||
      code.startsWith('addon_') ||
      selectedPrestationsList.some((p) => p.code && code === String(p.code).toLowerCase())
    );
  });

  if (prestationsFromState > 0 && !alreadyHasPrestationsLine) {
    itemsForDisplay.push({
      role: 'Supplement',
      kind: 'addon',
      code: 'ADDONS',
      ref: 'ADDONS',
      description: 'Prestations',
      quantity: null,
      unit: null,
      total: prestationsFromState,
    });
  }

  /* 4) TOTAL: somme des lignes après mutations
        - si on a des lignes PGI → somme des lignes
        - sinon fallback stay/fees/taxes + extras
  */

  const roomSupplementFromState = normalizeMoney(
    extras?.room ?? selectedRoom?.supplement ?? 0,
  );

  const totalFromItemsBase = hasPGI
    ? Number(itemsForDisplay.reduce((s, it) => s + safeNum(it.total, 0), 0).toFixed(2))
    : 0;

  const totalFromItems = hasPGI
    ? Number((totalFromItemsBase + roomDeltaFromState).toFixed(2))
    : 0;
  // ➜ somme des lignes dont le rôle = "tax"
  const taxesFromItems = hasPGI
    ? Number(
        itemsForDisplay
          .filter(isTax)
          .reduce((s, it) => s + safeNum(it.total, 0), 0)
          .toFixed(2),
      )
    : 0;

  // ➜ "Séjour et prestations incluses" = total - taxes
  const luggageFromState = normalizeMoney(selectedFlights?.luggagesTotal || 0);
  const sejourFromItems = hasPGI
    ? Math.max(
        0,
        Number(
          (totalFromItems - taxesFromItems - luggageFromState - prestationsFromState).toFixed(
            2,
          ),
        ),
      )
    : 0;

  // frais de dossier éventuels (même logique que ton fallback)
  const feesAmount = normalizeMoney(price?.fees);

  const fallbackTotal =
    normalizeMoney(price?.stay) +
    normalizeMoney(price?.fees) +
    normalizeMoney(price?.taxes) +
    normalizeMoney(extras?.room) +
    normalizeMoney(extras?.transportDelta) +
    normalizeMoney(extras?.luggagesTotal) +
    normalizeMoney(extras?.mealPlan && !extras.mealPlan.included ? extras.mealPlan.total : 0) +
    prestationsFromState;

  const totalAffiche =
    hasPGI && totalFromItems > 0 ? totalFromItems : Number(fallbackTotal.toFixed(2));

  const discountPercent = safeNum(discount, 0);
  const discountAmount =
    discountPercent > 0 ? Number(((totalAffiche * discountPercent) / 100).toFixed(2)) : 0;

  const totalApresRemise = Math.max(0, Number((totalAffiche - discountAmount).toFixed(2)));

  const start = search.startDate ? new Date(search.startDate) : null;
  const end = search.endDate ? new Date(search.endDate) : null;

  const travellersLine = (s = {}) => {
    const a = Number(s.adults || 0);
    const c = Number(s.children || 0);
    const i = Number(s.infants || 0);
    const parts = [];
    if (a) parts.push(`${a} adulte(s).`);
    if (c) parts.push(`${c} enfant(s)`);
    if (i) parts.push(`${i} bébé(s)`);
    return parts.join(' · ') || '—';
  };

  return (
    <div className="card sticky-top" style={{ top: '7rem', zIndex: 0 }}>
      {(imgSrc || title) && (
        <div className="position-relative">
          {imgSrc && (
            <img
              src={imgSrc}
              alt={title}
              className="card-img-top"
              style={{
                objectFit: 'cover',
                height: 250,
                borderTopLeftRadius: '0.375rem',
                borderTopRightRadius: '0.375rem',
              }}
            />
          )}
          <div
            className="px-3 py-2"
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              background:
                'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,.55) 60%, rgba(0,0,0,.70) 100%)',
              color: '#fff',
            }}
          >
            <h3 className="fw-semibold" style={{ color: '#fff', fontWeight: '300' }}>
              {title}
            </h3>
            {city ? <div className="small opacity-75">{city}</div> : null}
          </div>
        </div>
      )}

      <div className="card-body">
        {!imgSrc && (
          <>
            <h3 className="card-title mb-0">{title}</h3>
            {city ? (
              <div className="text-muted small mb-3">{city}</div>
            ) : (
              <div className="mb-3" />
            )}
          </>
        )}

        {/* Infos séjour */}
        <div className="small">
          <div className="d-flex justify-content-between py-2 border-bottom">
            <span className="text-primary">Date de départ</span>
            <span>{formatDateLongFR(start)}</span>
          </div>

          <div className="d-flex justify-content-between py-2 border-bottom">
            <span className="text-primary">Date de retour</span>
            <span>{formatDateLongFR(end)}</span>
          </div>
          <div className="d-flex justify-content-between py-2 border-bottom">
            <span className="text-primary">Durée</span>
            <span>
              {start && end
                ? `${Math.max(1, dd(start, end) + 1)} jours /  ${dd(start, end)} nuits`
                : '-'}
            </span>
          </div>
          <div className="d-flex justify-content-between py-2 border-bottom">
            <span className="text-primary">Ville de départ</span>
            <span>{search.departCity || '-'}</span>
          </div>
          <div className="d-flex justify-content-between py-2 border-bottom">
            <span className="text-primary">Voyageurs</span>
            <span>{travellersLine(search)}</span>
          </div>
        </div>

        <div className="mt-4">
          <h3 className="fw-semibold text-dark" style={{ fontWeight: '300' }}>
            Devis
          </h3>

          {quoteLoading ? (
            <InlineLoader show label="Mise à jour du devis…" />
          ) : (
            <>
              {hasPGI ? (
                <>
                  <div className="d-flex justify-content-between py-2 border-bottom small">
                    <span className="text-primary">Séjour</span>
                    <span>{fmtMoney(sejourFromItems, currency)}</span>
                  </div>

                  {luggageFromState > 0 && (
                    <div className="d-flex justify-content-between py-2 border-bottom small">
                      <span className="text-primary">Bagages</span>
                      <span>{fmtMoney(luggageFromState, currency)}</span>
                    </div>
                  )}

                  {prestationsFromState > 0 && (
                    <div className="d-flex justify-content-between py-2 border-bottom small">
                      <span className="text-primary">Prestations</span>
                      <span>{fmtMoney(prestationsFromState, currency)}</span>
                    </div>
                  )}

                  {feesAmount > 0 && (
                    <div className="d-flex justify-content-between py-2 border-bottom small">
                      <span className="text-primary">Frais de dossier</span>
                      <span>{fmtMoney(feesAmount, currency)}</span>
                    </div>
                  )}

                  <div className="d-flex justify-content-between py-2 border-bottom small">
                    <span className="text-primary">Taxes</span>
                    <span>{fmtMoney(taxesFromItems, currency)}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="d-flex justify-content-between py-2 border-bottom small">
                    <span className="text-primary">Séjour</span>
                    <span>{fmtMoney(price.stay, currency)}</span>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom small">
                    <span className="text-primary">Frais de dossier</span>
                    <span>{fmtMoney(price.fees, currency)}</span>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom small">
                    <span className="text-primary">Taxes</span>
                    <span>{fmtMoney(price.taxes, currency)}</span>
                  </div>

                  {prestationsFromState > 0 && (
                    <div className="d-flex justify-content-between py-2 border-bottom small">
                      <span className="text-primary">Prestations</span>
                      <span>{fmtMoney(prestationsFromState, currency)}</span>
                    </div>
                  )}
                </>
              )}

              <div className="d-flex justify-content-between pt-3 mt-2">
                <div className="fw-semibold">Sous-total</div>
                <div className="fw-bold">{fmtMoney(totalAffiche, currency)}</div>
              </div>

              {discountAmount > 0 && (
                <div className="d-flex justify-content-between pt-2 small">
                  <span className="text-primary">Remise B2B ({discountPercent}%)</span>
                  <span className="fw-semibold">- {fmtMoney(discountAmount, currency)}</span>
                </div>
              )}

              <div className="d-flex justify-content-between pt-2">
                <h3 className="fw-semibold text-dark" style={{ fontWeight: '300' }}>
                  TOTAL
                </h3>
                <h3 className="fw-bold fs-5 text-dark" style={{ fontWeight: '300' }}>
                  {fmtMoney(discountAmount > 0 ? totalApresRemise : totalAffiche, currency)}
                </h3>
              </div>
            </>
          )}
        </div>

        {typeof onContinue === 'function' && (
          <button type="button" className="btn btn-primary w-100 mt-3" onClick={onContinue}>
            Continuer vers paiement
          </button>
        )}
      </div>
    </div>
  );
}
